"use client";

import { isBefore, isAfter, differenceInMonths } from "date-fns";
import { usePricelist } from "@/pricelist/PricelistContext";
import {
  BookItem,
  DigitalServiceItem,
  ProductBundleItem,
  SubscriptionItem,
} from "../types";
import { getPriceOffer } from "@/products/pricing";
import { Nullable } from "@/types";
import { ProductListSection } from "@/cms/sanity.types";
import { formatPublicationDate } from "@/utils/formatDate";
import { IndexDocument } from "@cappelendamm/catalogue/meilisearch/search-index-receiver.js";
import { Tag } from "@cappelendamm/design-system/react";
import { type PricelistForMemberships } from "@/utils/getPricelistForUserMemberships";
import type { ItemListingTag as ListingTag } from "./types";

export type TaggableItem =
  BookItem | SubscriptionItem | ProductBundleItem | DigitalServiceItem;

export type ListingTagsConfig = Nullable<
  Pick<
    NonNullable<ProductListSection["fields"]>,
    | "showHighlighted"
    | "showPromoted"
    | "showInformational"
    | "showPublicationDate"
  >
>;

function isRecent(publicationDate: string | null): boolean {
  if (!publicationDate) return false;

  const pubDate = new Date(publicationDate);
  const now = new Date();

  // Recent if it's in the past AND within the last 3 months
  return isBefore(pubDate, now) && differenceInMonths(now, pubDate) <= 3;
}

function isUpcoming(publicationDate: string | null): boolean {
  if (!publicationDate) return false;

  const pubDate = new Date(publicationDate);
  const now = new Date();

  return isAfter(pubDate, now);
}

function getTagsFromTopics(
  topics: IndexDocument["etiketter"],
  type: "informational" | "highlighted",
): ListingTag[] {
  if (!topics) return [];

  return Object.values(topics)
    .flat()
    .filter((value) => value.includes(`/${type}/`))
    .map((value) => value.split("/").at(-1)!)
    .filter(Boolean)
    .map((label) => ({ label, type }));
}

/**
 * Computes the tag labels for a listing item based on the enabled tag config
 * and the applicable pricelists. Pure and side-effect free so it can be reused
 * outside of React rendering (e.g. to decide whether to reserve a layout row).
 */
export function getListingTags(
  item: TaggableItem,
  config: ListingTagsConfig | undefined,
  applicablePricelists: PricelistForMemberships[] | undefined,
): ListingTag[] {
  const tags: ListingTag[] = [];

  const showHighlighted = config?.showHighlighted ?? true;
  const showInformational = config?.showInformational ?? true;
  const showPromoted = config?.showPromoted ?? true;
  const showPublicationDate = config?.showPublicationDate ?? false;
  const safePricelists = Array.isArray(item.priceLists) ? item.priceLists : [];

  // Optional "I salg ..." label based on formatted publication date.
  if (showPublicationDate && item.publicationDate) {
    const formattedPublicationDate = formatPublicationDate(
      item.publicationDate,
    );
    if (formattedPublicationDate) {
      tags.push({
        label: `I salg ${formattedPublicationDate}`,
        type: "informational",
      });
    }
  }

  // Informational tags: time-based labels first, then topic-driven labels.
  if (showInformational) {
    // "Kommer snart" and "Nyhet" are mutually exclusive, so only one of them is added.
    if (isUpcoming(item.publicationDate) && !showPublicationDate) {
      tags.push({ label: "Kommer snart", type: "informational" });
    } else if (isRecent(item.publicationDate)) {
      tags.push({ label: "Nyhet", type: "informational" });
    }

    tags.push(...getTagsFromTopics(item.etiketter, "informational"));
  }

  // Promotion tags use the price offer computed from applicable pricelists only.
  if (showPromoted) {
    const offer = getPriceOffer({
      originalItemPrice: item.price,
      subjectToVAT: item.subjectToVAT,
      priceLists: safePricelists.filter((pricelist) =>
        applicablePricelists?.some(
          (applicable) => applicable.node?.identifier === pricelist.identifier,
        ),
      ),
    });

    if (offer.current === 0) {
      tags.push({ label: "Gratis", type: "promoted" });
    } else if (offer.hasOffer) {
      tags.push({ label: "Tilbud", type: "promoted" });
    }
  }

  // Highlighted tags are added last so they do not override promo/info/date tags.
  if (showHighlighted) {
    tags.push(...getTagsFromTopics(item.etiketter, "highlighted"));
  }

  // Show a maximum of 3 tags, listed horizontally.
  return tags.slice(0, 3);
}

/**
 * Shows labels on a product card (like "Nyhet" or "Tilbud").
 * Up to 3 labels are shown, listed horizontally.
 * "Tilbud" only shows up if the current user actually has access to that price.
 */
export function ListingTags({
  item,
  config,
}: {
  item: TaggableItem;
  config?: ListingTagsConfig;
}) {
  const { applicablePricelists } = usePricelist();

  const visibleTags = getListingTags(item, config, applicablePricelists);
  if (visibleTags.length === 0) return null;

  return (
    <div className="no-scrollbar flex min-w-0 flex-row flex-nowrap gap-2 overflow-auto">
      {visibleTags.map((tag, index) => (
        <Tag
          key={`${tag.type}-${tag.label}-${index}`}
          variant={tag.type}
          size="small"
          className="shrink-0"
        >
          {tag.label}
        </Tag>
      ))}
    </div>
  );
}
